import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { setCellValueWithFont, setupWorksheetForPrint, setWorksheetPrintArea } from './reportEventExcelUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import {
    addChartererAndDateTime,
    addOdfjellHeader,
    addVesselDetails,
    getCargoOperationForEvent,
    getChartererName,
    getWeightStr,
    odfjellHeaderColor,
    replaceLoadDischargePhrase,
} from './parts/helpers';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';

const addReportHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number) => {
    let rowNum = addOdfjellHeader(worksheet, logoImageId);
    rowNum = 6;
    setCellValueWithFont(worksheet, rowNum, 1, `DEADFREIGHT CLAIM`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 10,
    });
    rowNum++;
    return rowNum + 1;
};

const addDiscrepancyPhrase = (worksheet: ExcelJS.Worksheet, cargoOperation: RotationCargoOperation, rowNum: number) => {
    rowNum++;
    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        `Please be advised that according to the terms of the C/P dated ${prettifyDate(cargoOperation.cpDate)} between Odfjell Tankers AS and ${getChartererName(cargoOperation)
        }, the vessel has been fixed for ${replaceLoadDischargePhrase(cargoOperation, false, 'ing')} of ${getWeightStr(
            cargoOperation,
            false
        )}mt 2% moloo ${cargoOperation.cargo.product.tradeName}. `,
        {
            mergeAcross: 9,
            mergeDown: 3,
            verticalAlign: 'top',
            wrapText: true,
            align: 'left',
        }
    );
    rowNum += 4;
    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        'Upon tendering of Notice of Readiness on the vessel`s arrival I nominated to load the quantity below and have provided tank space accordingly.',
        {
            mergeAcross: 9,
            mergeDown: 2,
            align: 'left',
            wrapText: true,
            verticalAlign: 'top',
        }
    );
    return rowNum++;
};

const addDiffFigures = (worksheet: ExcelJS.Worksheet, event: TankersEvent, cargoOperation: RotationCargoOperation, rowNum: number): number => {
    const shipFigureInputDecimal = event.eventTypeInputRegList.find((o) => o.eventTypeInputId == 46)?.decimalValueReg;
    const nominatedWeight = cargoOperation.nominatedWeight;

    if (!shipFigureInputDecimal) return rowNum;

    rowNum += 3;

    const lines = [
        { label: 'According to calculations the vessel received :', value: shipFigureInputDecimal },
        { label: 'The vessel nominated for loading :', value: nominatedWeight },
        { label: 'Short shipped quantity :', value: shipFigureInputDecimal - nominatedWeight },
    ];

    lines.forEach((l, idx) => {
        const isLast = idx === lines.length - 1;
        const bottomBorder = { border: { bottom: { style: isLast ? 'double' : 'thin' } } } as const;
        setCellValueWithFont(worksheet, rowNum, 1, l.label, { bold: true, align: 'left', mergeAcross: 6 });
        setCellValueWithFont(worksheet, rowNum, 8, l.value, { align: 'right', ...bottomBorder });
        setCellValueWithFont(worksheet, rowNum, 9, 'Mt', { align: 'center', ...bottomBorder });
        rowNum++;
    });

    rowNum += 2;

    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        'In conformity with terms of the C/P, please be advised that I, Captain, as Master of the vessel and on behalf of Odfjell Tankers, hereby hold you responsible for deadfreight for the short shipped quantity.',
        { align: 'left', mergeAcross: 9, mergeDown: 2, verticalAlign: 'top', wrapText: true }
    );

    return rowNum++;
};

const setDefaultColumnWidths = (workheet: ExcelJS.Worksheet) => {
    workheet.getColumn(1).width = 9;
    workheet.getColumn(2).width = 2;
    workheet.getColumn(3).width = 14;
    workheet.getColumn(4).width = 21;
    workheet.getColumn(5).width = 9;
    workheet.getColumn(6).width = 17;
    workheet.getColumn(7).width = 10;
    workheet.getColumn(8).width = 14;
    workheet.getColumn(9).width = 7;
};

export const generateProtestDeadfreightClaimReport = (
    workbook: ExcelJS.Workbook,
    cargoEventReportData: CargoEventReportData,
    logoImageId: number
): void => {
    const _worksheet = workbook.addWorksheet('Deadfreight Claim');
    setupWorksheetForPrint(_worksheet);
    // Hide gridlines and keep a clean white background
    _worksheet.views = [{ state: 'normal', showGridLines: false }];
    const portCall = cargoEventReportData.selectedPortCall;
    const selectedEvent = cargoEventReportData.tankersEvents.find((ev) => !ev.deleted && ev.eventId === cargoEventReportData.excelReportInfo.eventId);
    const cargoOperation = getCargoOperationForEvent(
        portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations),
        selectedEvent
    );
    if (!cargoOperation || !selectedEvent) return;
    setDefaultColumnWidths(_worksheet);
    addReportHeader(_worksheet, logoImageId);
    let rowNum = addVesselDetails(
        _worksheet,
        9,
        1,
        7,
        portCall.voyageNumber,
        portCall.portDisplayName,
        cargoEventReportData.vessel,
        selectedEvent.cargoNumber,
        selectedEvent.eventDate.actualDateTimeLocal
    );
    rowNum = addDiscrepancyPhrase(_worksheet, cargoOperation, rowNum + 3);
    rowNum = addDiffFigures(_worksheet, selectedEvent, cargoOperation, rowNum);
    const endRow = addChartererAndDateTime(_worksheet, rowNum + 8, cargoOperation, selectedEvent, 'Master/Agent');
    setWorksheetPrintArea(_worksheet, endRow, 9);
};
